import _get from "lodash/get"
import qs from "qs"
import { apim } from "@/constants/api"
import {
  getPdpTooltipEndPoint,
  searchPdpEndPoint,
  searchPlpEndPoint,
  SEARCH_PDP_FL,
} from "@/constants/index"
import { CATALOG_PROJECTION_ENDPOINT } from "@/constants/routes"
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { HYDRATE } from "next-redux-wrapper"
import { addToRecentlyViewed, getPdpFLs, getUserPersona } from "@/utils/helper"
import { getInitialSku } from "@/components/ProductDetail/v1/productDetailsHelper"
import { getConfig } from "@/constants/config"

const initialState = {
  pdp: {},
  isDiscontinued: false,
  productInfo: {},
  teaserContent: [],
  isTeaserLoaded: false,
  asTabDetails: {},
  productInfo: null,
  ctData: [],
  leadTimeTab: {},
}
export const searchPdp = createAsyncThunk(
  "pdp/searchPdp",
  async (payload, thunkAPI) => {
    try {
      const {
        slug,
        isParts,
        selectedSku,
        selectedColor,
        setSkuVariantCb,
        getVariantFieldsCb,
        lwAppName = "",
      } = payload
      const persona = getUserPersona()
      const config = await getConfig()
      const language = config?.internationalization?.language ?? "en"
      const response = await apim.post(`${searchPdpEndPoint}/${slug}`, {
        q: "*:*",
        fl: getPdpFLs(persona).join(","),
        fq: `language_s:("${language}")`,
        collections: isParts ? "service_parts" : lwAppName?.toLowerCase(),
        // profilename: isParts
        //   ? `profile_${lwAppName?.toLowerCase()}_PDP`
        //   : `profile_service_parts_PDP`,

        profilename: !isParts
          ? "profile_annsacks_PDP"
          : `profile_service_parts_PDP`,
        search_type: "product_detail",
      })
      const {
        data: {
          response: { docs = [] } = {},
          fusion: { BundleSKUDetails: bundleIncludedItems = [] } = {},
        } = {},
      } = response
      const productData = docs.at(0)
      const sku =
        selectedSku === "" ||
        !productData["variantList.sku_ss"].some(v => v === selectedSku)
          ? getInitialSku({ productData, setSkuVariantCb, selectedColor })
          : selectedSku
      const customerFacingSKU = productData["CustomerFacingSKU_s"]

      const fusionQueryId = _get(response.data, "fusion.fusionQueryId")
      if (productData["variantList.sku_ss"]) {
        getVariantFieldsCb(productData["variantList.sku_ss"])
      }

      let recommendedAccessories = []
      if (productData["ProductProductRecommendedAccessories_ss"]) {
        recommendedAccessories =
          productData["ProductProductRecommendedAccessories_ss"]
      } else if (productData["ProductProductRecommendedAccessories_s"]) {
        recommendedAccessories.push(
          productData["ProductProductRecommendedAccessories_s"]
        )
      }

      let colors = []
      if (productData["Color.SKU.Details_ss"]) {
        colors = productData["Color.SKU.Details_ss"].map(color => {
          const d = color.split("|")
          return {
            sku: d[2],
            discontinued: !(d[4] === "false" || Date.parse(d[4]) > Date.now()),
          }
        })
      }

      addToRecentlyViewed(productData["ctId_s"])

      return {
        bundleIncludedItems,
        productData,
        sku,
        recommendedAccessories,
        colors,
        fusionQueryId,
        customerFacingSKU,
        slug,
      }
    } catch (err) {
      return err
    }
  }
)

export const getVariantFields = createAsyncThunk(
  "pdp/getVariantFields",
  async payload => {
    try {
      const { skuList = [], slug, isParts, lwAppName = "" } = payload
      let skus = ""
      skuList.forEach(s => (skus += `"${s}" `))

      const config = await getConfig()
      const language = config?.internationalization?.language ?? "en"

      const response = await apim.post(`${searchPdpEndPoint}/${slug}`, {
        q: "*:*",
        fq: `sku_s:(${skus.trim()}),language_s:("${language}")`,
        fl: "sku_s,RegionStatesExcluded_ss,RegionSpecialOrder_s",
        collections: isParts ? "service_parts" : lwAppName.toLowerCase(),
        profilename: isParts
          ? `profile_service_parts_PDP`
          : `profile_${lwAppName.toLowerCase()}_PDP`,
        search_type: "product_detail",
      })

      return response.data
    } catch (err) {
      return err
    }
  }
)

export const getProductBySlug = createAsyncThunk(
  "pdp/getProductBySlug",
  async slug => {
    let product = {}
    const CONFIG = await getConfig()
    const brandName = CONFIG.general.siteName.toLowerCase()

    try {
      let queryString = `${CATALOG_PROJECTION_ENDPOINT}?where=slug(en-US%3D"${slug}")`
      if (brandName === "annsacks") {
        queryString += `&brand=${brandName}`
      }
      const response = await apim.get(queryString)

      if (response && response.data && response.data.results) {
        product = response.data.results[0] ?? {}
      }
    } catch (err) {
      return err
    }

    return product
  }
)

export const getProductBySlugAndSku = createAsyncThunk(
  "pdp/getProductBySlugAndSku",
  async payload => {
    const { slug, skus } = payload
    let query = ""
    let masterQuery = ""
    if (slug) masterQuery += `slug(en-US%3D"${slug}") or `
    if (skus.length > 0) {
      skus.forEach((sku, i) => {
        query += `"${sku}"`
        if (i + 1 !== skus.length) query += ", "
      })
      masterQuery += `masterVariant(sku in (${query})) or variants(sku in (${query}))`
    }
    try {
      const res = await apim.get(
        `${CATALOG_PROJECTION_ENDPOINT}?where=${masterQuery}`
      )
      if (res && res.data && res.data.results) return res.data.results
    } catch (err) {
      console.error("Failed to fetch product details", err)
    }
    return []
  }
)

export const getReqItemLwData = createAsyncThunk(
  "pdp/getReqItemLwData",
  async payload => {
    const { query, lwAppName = "" } = payload
    const config = await getConfig()
    const language = config?.internationalization?.language ?? "en"
    const response = await apim.get(searchPlpEndPoint, {
      params: {
        q: "*:*",
        fl: "ctId_s, RegionOnBackOrder_s",
        fq: [
          `ctId_s:(${query.trim()})`,
          'masterVariant_b:("true")',
          `language_s:("${language}")`,
        ],
        collections: lwAppName.toLowerCase(),
        profilename: `profile_${lwAppName.toLowerCase()}_General`,
      },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: "repeat", encode: false })
      },
    })
    return response?.data?.response?.docs ?? []
  }
)

export const fetchPdpRequiredItems = createAsyncThunk(
  "pdp/fetchPdpRequiredItems",
  async query => {
    try {
      const response = await apim.get(
        `${CATALOG_PROJECTION_ENDPOINT}?where=id in (${query})`
      )
      return response.data.results ?? []
    } catch (err) {
      return err
    }
  }
)

export const getPdpTooltip = createAsyncThunk(
  "pdp/getPdpTooltip",
  async payload => {
    const { brandName } = payload
    const response = await apim.get(getPdpTooltipEndPoint, {
      params: {
        collection: brandName?.toLowerCase(),
      },
    })
    return response?.data
  }
)

export const pdpSlice = createSlice({
  name: "pdp",
  initialState,
  reducers: {
    setPDP(state, action) {
      state.pdp = { ...state.pdp, ...action.payload }
    },
    setLeadTimeTab(state, action) {
      state.leadTimeTab = { ...state.leadTimeTab, ...action.payload }
    },
    getDiscontinuedProductValue(state, action) {
      state.isDiscontinued = action.payload
    },
    setProductInfo(state, action) {
      state.productInfo = action.payload
    },
    setTeaserContent(state, action) {
      state.teaserContent = action.payload
    },
    setIsTeaserLoaded(state, action) {
      state.isTeaserLoaded = action.payload
    },
    getAsTabDetails(state, action) {
      state.asTabDetails = action.payload
    },
    updateVariantSkuInTabDetails(state, action) {
      state.asTabDetails.selectedVariantSku = action.payload.sku
      state.asTabDetails.priceList =
        action.payload?.listPrice || state.asTabDetails?.priceList
    },
  },
  extraReducers(builder) {
    builder
      .addCase([HYDRATE], (state, action) => {
        return {
          ...state,
          ...action.payload.pdp,
        }
      })
      .addCase(searchPdp.pending, (state, action) => {
        state.status = "pending"
      })
      .addCase(searchPdp.fulfilled, (state, action) => {
        const {
          productData = {},
          sku = "",
          recommendedAccessories = [],
          colors = [],
          slug = "",
          customerFacingSKU = "",
          bundleIncludedItems = [],
        } = action.payload
        state.status = "succeeded"
        state.pdp = {
          sku,
          slug: slug,
          recommendedAccessories,
          productBrand: productData["ProductBrandNameDisplay_s"],
          productNo: productData["ProductProductNo_s"],
          data: productData,
          variants: colors,
          customerFacingSKU: customerFacingSKU
            ? customerFacingSKU.replace("K-", "")
            : "",
          bundleIncludedItems,
        }
      })
      .addCase(searchPdp.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(getProductBySlugAndSku.pending, state => {
        state.status = "pending"
      })
      .addCase(getProductBySlugAndSku.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.productServices = action.payload
      })
      .addCase(getProductBySlugAndSku.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(getProductBySlug.pending, state => {
        state.status = "pending"
      })
      .addCase(getProductBySlug.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(getProductBySlug.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.ctData = action.payload
      })
  },
})

export const {
  setPDP,
  getDiscontinuedProductValue,
  setProductInfo,
  setTeaserContent,
  setIsTeaserLoaded,
  getAsTabDetails,
  updateVariantSkuInTabDetails,
  setLeadTimeTab
} = pdpSlice.actions
export const selectPdpState = state => state.pdp
export default pdpSlice.reducer
